/**
 * ProjectDetailPage — project resources / user groups / meetings / board links
 * (epic-6 §3). Resources are attached by type picker (ORG-3/PRJ-4).
 */
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, FolderKanban, Users, Video, KanbanSquare, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetProjectQuery,
  useGetProjectResourcesQuery,
  useAttachProjectResourceMutation,
  useDetachProjectResourceMutation,
  useGetProjectUserGroupsQuery,
  useAttachProjectUserGroupMutation,
  useDetachProjectUserGroupMutation,
} from '@/store/api/projectsApi';
import { useGetUserGroupsQuery } from '@/store/api/organizationsApi';
import type { ProjectResource, ProjectResourceType } from '@/types';
import { formatDateSafe } from '@/lib/date';

const RESOURCE_TYPE_LABELS: Record<ProjectResourceType, string> = {
  meeting: 'Meeting',
  planning: 'Planning',
  'storage-collection': 'Collection',
  repository: 'Repository',
  conversation: 'Conversation',
  'indexer-source': 'Indexer source',
  voice: 'Voice',
  file: 'File',
};

const RESOURCE_TYPE_OPTIONS: ProjectResourceType[] = [
  'meeting',
  'planning',
  'storage-collection',
  'repository',
  'voice',
];

function resourceLink(r: ProjectResource): string {
  switch (r.type) {
    case 'meeting':
      return `/meetings`;
    case 'planning':
      return `/projects/${r.projectId}/board`;
    case 'storage-collection':
      return `/storage/collections/${r.refId}/files`;
    case 'repository':
      return `/repositories/${r.refId}/branches`;
    case 'conversation':
      return `/conversations/${r.refId}`;
    case 'voice':
      return `/voices`;
    case 'indexer-source':
      return `/indexer`;
    case 'file':
      return `/storage/collections`;
    default:
      return '#';
  }
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: project } = useGetProjectQuery(projectId ?? '', { skip: !projectId });
  const { data: resources, isLoading: resourcesLoading } = useGetProjectResourcesQuery({ id: projectId ?? '' }, { skip: !projectId });
  const { data: groups } = useGetProjectUserGroupsQuery(projectId ?? '', { skip: !projectId });
  const { data: allGroups } = useGetUserGroupsQuery();

  const [attachOpen, setAttachOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [detachTarget, setDetachTarget] = useState<ProjectResource | null>(null);

  const [detachResource] = useDetachProjectResourceMutation();
  const [detachGroup] = useDetachProjectUserGroupMutation();

  const resourceList = (resources as ProjectResource[] | undefined) ?? [];
  const groupList = (groups as { _id: string; name: string }[] | undefined) ?? [];
  const allGroupList = (allGroups as { _id: string; name: string }[] | undefined) ?? [];

  const meetingResources = resourceList.filter((r) => r.type === 'meeting');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{project?.name ?? 'Project'}</h1>
          <p className="text-sm text-muted-foreground">{project?.description ?? 'Project resources and relations'}</p>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/projects/${projectId}/board`}>
            <KanbanSquare className="mr-1.5 h-4 w-4" /> View board
          </Link>
        </Button>
      </div>

      <Tabs defaultValue="resources">
        <TabsList>
          <TabsTrigger value="resources"><FolderKanban className="mr-1.5 h-3.5 w-3.5" /> Resources ({resourceList.length})</TabsTrigger>
          <TabsTrigger value="groups"><Users className="mr-1.5 h-3.5 w-3.5" /> User groups ({groupList.length})</TabsTrigger>
          <TabsTrigger value="meetings"><Video className="mr-1.5 h-3.5 w-3.5" /> Meetings ({meetingResources.length})</TabsTrigger>
          <TabsTrigger value="board"><KanbanSquare className="mr-1.5 h-3.5 w-3.5" /> Planning</TabsTrigger>
        </TabsList>

        {/* Resources */}
        <TabsContent value="resources" className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setAttachOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Attach resource
            </Button>
          </div>
          <Card>
            <CardContent className="pt-5">
              {resourcesLoading ? (
                <LoadingSpinner className="py-8" />
              ) : resourceList.length === 0 ? (
                <EmptyState title="No resources" description="Attach meetings, planning boards, collections, repositories or voices" />
              ) : (
                <div className="space-y-2">
                  {resourceList.map((r) => (
                    <div key={r._id} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{RESOURCE_TYPE_LABELS[r.type] ?? r.type}</Badge>
                        <span className="text-sm font-medium">{r.name ?? r.refId}</span>
                        <Link to={resourceLink(r)} className="text-xs text-muted-foreground hover:text-primary">
                          open <ExternalLink className="inline h-3 w-3" />
                        </Link>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">{formatDateSafe(r.addedAt, 'MMM d')}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetachTarget(r)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User groups */}
        <TabsContent value="groups" className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setGroupOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Attach group
            </Button>
          </div>
          <Card>
            <CardContent className="pt-5">
              {groupList.length === 0 ? (
                <EmptyState title="No user groups" description="Attach a user group to grant its members project access" />
              ) : (
                <div className="space-y-2">
                  {groupList.map((g) => (
                    <div key={g._id} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <span className="text-sm font-medium">{g.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={async () => {
                          try {
                            await detachGroup({ id: projectId!, groupId: g._id }).unwrap();
                            toast.success('Group detached');
                          } catch (err) {
                            toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed');
                          }
                        }}
                      >
                        <Trash2 className="mr-1 h-3 w-3 text-destructive" /> Detach
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meetings */}
        <TabsContent value="meetings" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Project meetings</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {meetingResources.length === 0 ? (
                <EmptyState title="No meetings" description="Attach meetings as resources to link transcript files and recordings" />
              ) : (
                <div className="space-y-2">
                  {meetingResources.map((r) => (
                    <div key={r._id} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{r.name ?? r.refId}</span>
                      </div>
                      <Link to={resourceLink(r)} className="text-xs text-muted-foreground hover:text-primary">
                        Meeting list / transcripts <ExternalLink className="inline h-3 w-3" />
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Planning board */}
        <TabsContent value="board" className="space-y-3">
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <KanbanSquare className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Planning tasks for this project live in the 5-column board
                (pending / in_progress / blocked / completed / cancelled).
              </p>
              <Button asChild>
                <Link to={`/projects/${projectId}/board`}>
                  Open planning board <ExternalLink className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Attach resource dialog */}
      {attachOpen && (
        <AttachResourceDialog projectId={projectId!} onClose={() => setAttachOpen(false)} onDone={() => setAttachOpen(false)} />
      )}

      {/* Attach group dialog */}
      {groupOpen && (
        <AttachGroupDialog projectId={projectId!} allGroups={allGroupList} attachedIds={groupList.map((g) => g._id)} onClose={() => setGroupOpen(false)} onDone={() => setGroupOpen(false)} />
      )}

      <ConfirmDialog
        open={!!detachTarget}
        onOpenChange={(open) => !open && setDetachTarget(null)}
        title="Detach Resource"
        description={`Detach "${detachTarget?.name ?? detachTarget?.refId}" from this project?`}
        confirmLabel="Detach"
        variant="destructive"
        onConfirm={async () => {
          if (detachTarget) {
            try {
              await detachResource({ id: projectId!, resourceId: detachTarget._id }).unwrap();
              toast.success('Resource detached');
            } catch (err) {
              toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to detach');
            }
            setDetachTarget(null);
          }
        }}
      />
    </div>
  );
}

// ─── Attach resource dialog ───────────────────────────────────────────────────

function AttachResourceDialog(props: { projectId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [attach, { isLoading }] = useAttachProjectResourceMutation();
  const [type, setType] = useState<ProjectResourceType>('planning');
  const [refId, setRefId] = useState('');
  const [name, setName] = useState('');

  const submit = async () => {
    if (!refId.trim()) {
      toast.error('Reference ID is required');
      return;
    }
    try {
      await attach({
        id: props.projectId,
        body: { type, refId: refId.trim(), name: name.trim() || undefined },
      }).unwrap();
      toast.success('Resource attached');
      props.onDone();
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to attach');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach resource</DialogTitle>
          <DialogDescription>Link a resource of the chosen type to this project.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Resource type</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={type}
              onChange={(e) => setType(e.target.value as ProjectResourceType)}
            >
              {RESOURCE_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{RESOURCE_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Reference ID</Label>
            <Input placeholder={type === 'planning' ? 'projectId or board id' : 'id of the resource'} value={refId} onChange={(e) => setRefId(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Display name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button onClick={submit} disabled={isLoading}>{isLoading ? 'Attaching…' : 'Attach'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Attach group dialog ──────────────────────────────────────────────────────

function AttachGroupDialog(props: { projectId: string; allGroups: Array<{ _id: string; name: string }>; attachedIds: string[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [attach, { isLoading }] = useAttachProjectUserGroupMutation();
  const [selected, setSelected] = useState('');

  const available = props.allGroups.filter((g) => !props.attachedIds.includes(g._id));

  const submit = async () => {
    if (!selected) {
      toast.error('Select a group');
      return;
    }
    try {
      await attach({ id: props.projectId, groupId: selected }).unwrap();
      toast.success('Group attached');
      props.onDone();
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to attach group');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach user group</DialogTitle>
          <DialogDescription>Grant project access to all members of the group.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>User group</Label>
          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">— select group —</option>
            {available.map((g) => (
              <option key={g._id} value={g._id}>{g.name}</option>
            ))}
          </select>
          {available.length === 0 && <p className="text-xs text-muted-foreground">All groups are already attached.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button onClick={submit} disabled={isLoading}>{isLoading ? 'Attaching…' : 'Attach'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}